/**
 * Tournament Scheduling Utility
 * Automatically assigns time slots and court numbers to matches
 */

import { Tournament, TournamentEvent, AdvancementRule } from "../types";

interface ScheduledMatch {
  matchId: string;
  startTime: string; // ISO string or HH:MM format
  courtNumber: number;
  duration: number; // minutes
  type: "group" | "knockout";
  groupIndex?: number;
  matchIndex?: number;
  bracketType?: "mastersCup" | "tourCup";
  team1Id?: string; // For group matches generated from teamIds
  team2Id?: string; // For group matches generated from teamIds
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  courtNumber: number;
  available: boolean;
  occupiedBy?: "event" | "match";
}

export interface PlayerMatchSchedule {
  playerId: string;
  playerName: string;
  matches: Array<{
    matchId: string;
    startTime: string;
    endTime: string;
    courtNumber: number;
    opponent: string;
    isGroupStage: boolean;
  }>;
  waitingTimes: number[]; // Minutes between consecutive matches
  averageWaiting: number;
  minWaiting: number;
  maxWaiting: number;
}

export interface WaitingTimeAnalysis {
  playerSchedules: PlayerMatchSchedule[];
  tournamentAverage: number;
  fairnessScore: number; // 0-100, higher is better (lower std deviation)
  warnings: string[];
}

/**
 * Detect players registered on more than one team in the same tournament.
 * Returns an array of violations — should be empty for a valid tournament.
 */
export function detectDuplicatePlayers(tournament: Tournament): Array<{
  playerName: string;
  teamNames: string[];
}> {
  const playerToTeams = new Map<string, { name: string; teams: string[] }>();

  (tournament.registeredTeams || []).forEach(team => {
    const teamLabel = team.teamName || `${team.player1Name} & ${team.player2Name}`;
    [
      { id: team.player1Id, name: team.player1Name },
      { id: team.player2Id, name: team.player2Name },
    ]
      .filter(p => p.id)
      .forEach(({ id, name }) => {
        if (!playerToTeams.has(id)) {
          playerToTeams.set(id, { name: name || id, teams: [] });
        }
        playerToTeams.get(id)!.teams.push(teamLabel);
      });
  });

  return Array.from(playerToTeams.values())
    .filter(v => v.teams.length > 1)
    .map(v => ({ playerName: v.name, teamNames: v.teams }));
}

/**
 * Calculate waiting times for all players in a tournament
 */
export function analyzePlayerWaitingTimes(tournament: Tournament): WaitingTimeAnalysis {
  const matchDuration = tournament.timedMatchDuration || 20;
  const playerMatchMap = new Map<string, Array<{
    startTime: Date;
    endTime: Date;
    courtNumber: number;
    matchId: string;
    opponent: string;
    isGroupStage: boolean;
  }>>();
  
  // Helper to get player details
  const getPlayerName = (playerId: string): string => {
    const team = tournament.registeredTeams?.find(t => 
      t.player1Id === playerId || t.player2Id === playerId
    );
    return playerId === team?.player1Id ? team?.player1Name || 'Unknown' : team?.player2Name || 'Unknown';
  };
  
  // Helper to get opponent name for a player
  const getOpponentName = (match: any, playerId: string): string => {
    const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
    const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
    
    if (team1?.player1Id === playerId || team1?.player2Id === playerId) {
      return team2?.teamName || `${team2?.player1Name} / ${team2?.player2Name}` || 'TBD';
    } else {
      return team1?.teamName || `${team1?.player1Name} / ${team1?.player2Name}` || 'TBD';
    }
  };
  
  // Collect all matches for each player from group stage
  if (tournament.groups) {
    tournament.groups.forEach(group => {
      group.matchResults?.forEach(match => {
        if (!match.startTime || !match.courtNumber) return;
        
        const startTime = new Date(match.startTime);
        const endTime = new Date(startTime.getTime() + matchDuration * 60 * 1000);
        
        const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
        const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
        
        const playerIds = [
          team1?.player1Id,
          team1?.player2Id,
          team2?.player1Id,
          team2?.player2Id
        ].filter(Boolean) as string[];
        
        playerIds.forEach(playerId => {
          if (!playerMatchMap.has(playerId)) {
            playerMatchMap.set(playerId, []);
          }
          playerMatchMap.get(playerId)!.push({
            startTime,
            endTime,
            courtNumber: match.courtNumber!,
            matchId: `group-${match.team1Id}-${match.team2Id}`,
            opponent: getOpponentName(match, playerId),
            isGroupStage: true,
          });
        });
      });
    });
  }
  
  // Collect knockout matches
  const knockoutBrackets = [
    ...(tournament.knockoutBracket?.mastersCup || []),
    ...(tournament.knockoutBracket?.tourCup || [])
  ];
  
  knockoutBrackets.forEach(match => {
    if (!match.startTime || !match.courtNumber || match.isPlaceholder) return;
    if (!match.team1Id || !match.team2Id) return;
    
    const startTime = new Date(match.startTime);
    const endTime = new Date(startTime.getTime() + matchDuration * 60 * 1000);
    
    const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
    const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
    
    const playerIds = [
      team1?.player1Id,
      team1?.player2Id,
      team2?.player1Id,
      team2?.player2Id
    ].filter(Boolean) as string[];
    
    playerIds.forEach(playerId => {
      if (!playerMatchMap.has(playerId)) {
        playerMatchMap.set(playerId, []);
      }
      playerMatchMap.get(playerId)!.push({
        startTime,
        endTime,
        courtNumber: match.courtNumber!,
        matchId: match.id,
        opponent: getOpponentName(match, playerId),
        isGroupStage: false,
      });
    });
  });
  
  // Calculate waiting times for each player
  const playerSchedules: PlayerMatchSchedule[] = [];
  let totalWaitingTime = 0;
  let totalWaitingCount = 0;
  const warnings: string[] = [];
  
  playerMatchMap.forEach((matches, playerId) => {
    // Sort matches by start time
    matches.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    
    // Calculate gaps between consecutive matches
    const waitingTimes: number[] = [];
    for (let i = 0; i < matches.length - 1; i++) {
      const gapMs = matches[i + 1].startTime.getTime() - matches[i].endTime.getTime();
      const gapMinutes = Math.round(gapMs / (1000 * 60));
      waitingTimes.push(gapMinutes);
      totalWaitingTime += gapMinutes;
      totalWaitingCount++;
    }
    
    const averageWaiting = waitingTimes.length > 0
      ? waitingTimes.reduce((a, b) => a + b, 0) / waitingTimes.length
      : 0;
    const minWaiting = waitingTimes.length > 0 ? Math.min(...waitingTimes) : 0;
    const maxWaiting = waitingTimes.length > 0 ? Math.max(...waitingTimes) : 0;

    playerSchedules.push({
      playerId,
      playerName: getPlayerName(playerId),
      matches: matches.map(m => ({
        matchId: m.matchId,
        startTime: m.startTime.toISOString(),
        endTime: m.endTime.toISOString(),
        courtNumber: m.courtNumber,
        opponent: m.opponent,
        isGroupStage: m.isGroupStage,
      })),
      waitingTimes,
      averageWaiting,
      minWaiting,
      maxWaiting,
    });
  });

  // Calculate tournament metrics
  const tournamentAverage = totalWaitingCount > 0 ? totalWaitingTime / totalWaitingCount : 0;

  // Fairness: measure how equal average waiting times are across players (lower std dev = fairer)
  const avgWaitings = playerSchedules
    .filter(p => p.waitingTimes.length > 0)
    .map(p => p.averageWaiting);

  const variance = avgWaitings.length > 0
    ? avgWaitings.reduce((sum, avg) => sum + Math.pow(avg - tournamentAverage, 2), 0) / avgWaitings.length
    : 0;
  const stdDev = Math.sqrt(variance);
  const fairnessScore = Math.max(0, Math.min(100, 100 - (stdDev * 2)));

  // Warn about players whose average wait deviates significantly from the tournament average
  playerSchedules
    .filter(p => p.waitingTimes.length > 0)
    .forEach(p => {
      const deviation = Math.abs(p.averageWaiting - tournamentAverage);
      if (deviation > 20) {
        warnings.push(`⚠️ ${p.playerName}: avg break ${Math.round(p.averageWaiting)}min (tournament avg: ${Math.round(tournamentAverage)}min)`);
      }
    });

  return {
    playerSchedules,
    tournamentAverage,
    fairnessScore,
    warnings,
  };
}

/**
 * Parse time string (HH:MM or ISO) to Date object
 */
function parseTime(timeStr: string, baseDate: Date = new Date()): Date {
  if (timeStr.includes("T")) {
    return new Date(timeStr);
  }
  const [hours, minutes] = timeStr.split(":").map(Number);
  const date = new Date(baseDate);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/**
 * Format Date to HH:MM string
 */
function formatTime(date: Date): string {
  return date.toTimeString().slice(0, 5);
}

/**
 * Format Date to ISO string
 */
function formatISO(date: Date): string {
  return date.toISOString();
}

/**
 * Check if a time slot conflicts with any events
 */
function hasEventConflict(
  startTime: Date,
  endTime: Date,
  events: TournamentEvent[],
  baseDate: Date,
): boolean {
  for (const event of events) {
    const eventStart = parseTime(event.startTime, baseDate);
    const eventEnd = new Date(eventStart.getTime() + event.duration * 60000);

    // Check for overlap
    if (
      (startTime >= eventStart && startTime < eventEnd) ||
      (endTime > eventStart && endTime <= eventEnd) ||
      (startTime <= eventStart && endTime >= eventEnd)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Generate all available time slots for the tournament day
 * Slots are interleaved by time: at each time slot, all courts are available
 */
function generateTimeSlots(
  tournament: Tournament,
  baseDate: Date,
): TimeSlot[] {
  if (!tournament.startTime || !tournament.numberOfCourts) {
    return [];
  }

  const slots: TimeSlot[] = [];
  const matchDuration = tournament.timedMatchDuration || 20;
  const changeoverTime = tournament.changeoverTime || 0; // Time between matches on same court
  const slotDuration = matchDuration + changeoverTime; // Total time per slot
  const events = tournament.events || [];

  // Start from tournament start time
  let currentTime = parseTime(tournament.startTime, baseDate);

  // End at midnight or a reasonable end time (e.g., 23:00)
  const endOfDay = new Date(baseDate);
  endOfDay.setHours(23, 0, 0, 0);

  // Generate slots by TIME first (not by court)
  // This ensures matches are distributed across all courts at each time
  let slotTime = new Date(currentTime);
  
  while (slotTime < endOfDay) {
    const slotEnd = new Date(slotTime.getTime() + matchDuration * 60000);

    // Check if this slot conflicts with any event
    const conflictingEvent = events.find(event => {
      const eventStart = parseTime(event.startTime, baseDate);
      const eventEnd = new Date(eventStart.getTime() + event.duration * 60000);
      return (
        (slotTime >= eventStart && slotTime < eventEnd) ||
        (slotEnd > eventStart && slotEnd <= eventEnd) ||
        (slotTime <= eventStart && slotEnd >= eventEnd)
      );
    });

    if (conflictingEvent) {
      // Skip over the entire event — jump to the event's end time (rounded up to
      // the next clean slot boundary so changeover time still applies).
      const eventStart = parseTime(conflictingEvent.startTime, baseDate);
      const eventEnd = new Date(eventStart.getTime() + conflictingEvent.duration * 60000);
      slotTime = new Date(eventEnd);
      continue;
    }

    // No conflict — create a slot for each court at this time
    for (let court = 1; court <= tournament.numberOfCourts; court++) {
      slots.push({
        startTime: formatISO(slotTime),
        endTime: formatISO(slotEnd),
        courtNumber: court,
        available: true,
        occupiedBy: undefined,
      });
    }

    // Move to next time slot (including changeover time)
    slotTime = new Date(slotTime.getTime() + slotDuration * 60000);
  }

  return slots;
}

/**
 * Generate round-robin matches from team IDs
 */
function generateRoundRobinMatches(teamIds: string[]): { team1Id: string; team2Id: string }[] {
  const matches: { team1Id: string; team2Id: string }[] = [];
  for (let i = 0; i < teamIds.length; i++) {
    for (let j = i + 1; j < teamIds.length; j++) {
      matches.push({ team1Id: teamIds[i], team2Id: teamIds[j] });
    }
  }
  return matches;
}

/**
 * Get all matches from tournament that need scheduling
 */
function getAllMatches(tournament: Tournament): ScheduledMatch[] {
  const matches: ScheduledMatch[] = [];
  const matchDuration = tournament.timedMatchDuration || 20;
  // Bye placeholder teams never actually play — any pairing involving one is a
  // walkover already resolved at generate-groups/bracket time, so it must never
  // consume a real time slot.
  const byeTeamIds = new Set(
    (tournament.registeredTeams || []).filter((t: any) => t.isBye).map((t) => t.id)
  );

  console.log("getAllMatches - Processing tournament:");
  console.log("  Groups count:", tournament.groups?.length || 0);

  // Group stage matches
  if (tournament.groups) {
    tournament.groups.forEach((group, groupIndex) => {
      console.log(`  Group ${groupIndex} (${group.name}):`, {
        teamIds: group.teamIds?.length || 0,
        hasMatchResults: !!group.matchResults,
        matchResultsCount: group.matchResults?.length || 0,
      });

      // Generate all possible matches from teamIds (round-robin), excluding any
      // pairing against a Bye team since those are walkovers, not real matches.
      if (group.teamIds && group.teamIds.length > 1) {
        const roundRobinMatches = generateRoundRobinMatches(group.teamIds)
          .filter((m) => !byeTeamIds.has(m.team1Id) && !byeTeamIds.has(m.team2Id));
        console.log(`    Generated ${roundRobinMatches.length} round-robin matches`);
        
        roundRobinMatches.forEach((match, matchIndex) => {
          // Check if this match already has a result with timing info
          const existingResult = group.matchResults?.find(
            (m) =>
              (m.team1Id === match.team1Id && m.team2Id === match.team2Id) ||
              (m.team1Id === match.team2Id && m.team2Id === match.team1Id)
          );
          
          const startTime = existingResult?.startTime || "";
          const courtNumber = existingResult?.courtNumber || 0;
          
          console.log(`    Match ${matchIndex}: ${match.team1Id} vs ${match.team2Id}`, {
            startTime,
            courtNumber,
            hasExistingResult: !!existingResult,
          });
          
          matches.push({
            matchId: `group-${groupIndex}-${matchIndex}`,
            startTime,
            courtNumber,
            duration: matchDuration,
            type: "group",
            groupIndex,
            matchIndex,
            team1Id: match.team1Id,
            team2Id: match.team2Id,
          });
        });
      }
    });
  }

  // Knockout matches — skip any Bye walkover, already auto-resolved with no real match to play.
  if (tournament.knockoutBracket?.mastersCup) {
    tournament.knockoutBracket.mastersCup.forEach((match: any, index) => {
      if (match.isWalkover || byeTeamIds.has(match.team1Id) || byeTeamIds.has(match.team2Id)) return;
      matches.push({
        matchId: `knockout-mastersCup-${index}`,
        startTime: match.startTime || "",
        courtNumber: match.courtNumber || 0,
        duration: matchDuration,
        type: "knockout",
        bracketType: "mastersCup",
        matchIndex: index,
        team1Id: match.team1Id,
        team2Id: match.team2Id,
      });
    });
  }

  if (tournament.knockoutBracket?.tourCup) {
    tournament.knockoutBracket.tourCup.forEach((match: any, index) => {
      if (match.isWalkover || byeTeamIds.has(match.team1Id) || byeTeamIds.has(match.team2Id)) return;
      matches.push({
        matchId: `knockout-tourCup-${index}`,
        startTime: match.startTime || "",
        courtNumber: match.courtNumber || 0,
        duration: matchDuration,
        type: "knockout",
        bracketType: "tourCup",
        matchIndex: index,
        team1Id: match.team1Id,
        team2Id: match.team2Id,
      });
    });
  }

  return matches;
}

const getPositionLabel = (pos: number): string => {
  if (pos === 0) return '1st';
  if (pos === 1) return '2nd';
  if (pos === 2) return '3rd';
  return `${pos + 1}th`;
};

/**
 * Builds pre-results placeholder descriptors for both brackets from AdvancementRules.
 * Mirrors the server's resolveAdvancementRules (padelBackend tournaments.ts), but since
 * actual team identity isn't known until group standings are final, 'best-of-position-
 * across-groups' picks become generic wildcard labels (e.g. "Best 2nd Place #1") instead
 * of a specific team, and 'remaining' lists whatever per-group slots aren't yet claimed —
 * dropping from the end of a position's group list to keep the count in sync with any
 * wildcard rule that already claimed some of that position's slots elsewhere.
 */
function buildAdvancementDescriptorLists(
  groups: NonNullable<Tournament['groups']>,
  groupNames: string[],
  mastersRules: AdvancementRule[],
  tourRules: AdvancementRule[]
): { mastersCup: string[]; tourCup: string[] } {
  const claimedGroupPositions = new Set<string>(); // "position-groupIndex"
  const wildcardBudgetByPosition = new Map<number, number>();

  const processRules = (rules: AdvancementRule[], out: string[]) => {
    rules.forEach(rule => {
      if (rule.type === 'group-position') {
        groups.forEach((_, g) => {
          const key = `${rule.position}-${g}`;
          if (claimedGroupPositions.has(key)) return;
          claimedGroupPositions.add(key);
          out.push(`${getPositionLabel(rule.position)} Group ${groupNames[g]}`);
        });
      } else if (rule.type === 'best-of-position-across-groups') {
        for (let i = 0; i < rule.count; i++) {
          out.push(`Best ${getPositionLabel(rule.position)} Place #${i + 1}`);
        }
        wildcardBudgetByPosition.set(rule.position, (wildcardBudgetByPosition.get(rule.position) || 0) + rule.count);
      } else {
        // Any position with a wildcard budget has an ambiguous leftover: which group's
        // team is NOT among the "best" picks isn't knowable until standings are final,
        // so those slots get a generic label instead of a specific group letter.
        const maxPosition = Math.max(0, ...groups.map(g => (g.teamIds?.length || 0) - 1));
        for (let p = 0; p <= maxPosition; p++) {
          const availableGroups = groups
            .map((_, g) => g)
            .filter(g => !claimedGroupPositions.has(`${p}-${g}`));
          const budget = wildcardBudgetByPosition.get(p) || 0;
          const keep = Math.max(0, availableGroups.length - budget);
          if (budget > 0) {
            for (let i = 0; i < keep; i++) {
              out.push(`${getPositionLabel(p)} Place #${i + 1}`);
            }
            availableGroups.forEach(g => claimedGroupPositions.add(`${p}-${g}`));
          } else {
            availableGroups.slice(0, keep).forEach(g => {
              claimedGroupPositions.add(`${p}-${g}`);
              out.push(`${getPositionLabel(p)} Group ${groupNames[g]}`);
            });
          }
        }
      }
    });
  };

  const mastersCup: string[] = [];
  const tourCup: string[] = [];
  processRules(mastersRules, mastersCup);
  processRules(tourRules, tourCup);
  return { mastersCup, tourCup };
}

/**
 * Generate placeholder team descriptions for knockout brackets
 * Based on the actual seeding algorithm used when generating brackets
 */
function generateKnockoutPlaceholders(
  tournament: Tournament,
  bracketType: 'mastersCup' | 'tourCup',
  expectedMatches: number
): Array<{ team1Placeholder: string; team2Placeholder: string; round: string; previousMatch1?: number; previousMatch2?: number; isWalkover?: boolean }> {
  const groups = tournament.groups || [];
  const groupCount = groups.length;

  // For single-bracket tournaments every advancing team is "1st" (only 1 advances per group)
  const isSingleBracket = tournament.knockoutFormatType === 'single-bracket';
  const knockoutAdvanceCount = tournament.knockoutAdvanceCount || 1;
  const mastersCupAdvanceCount = isSingleBracket ? knockoutAdvanceCount : (tournament.mastersCupAdvanceCount || 2);
  const tourCupAdvanceCount = isSingleBracket ? 0 : (tournament.tourCupAdvanceCount || 2);

  // Get group names (A, B, C, D, etc.)
  const groupNames = groups.map((g, i) => String.fromCharCode(65 + i));

  // A Bye placeholder team always finishes last in its group (it can never win a real
  // point), so "Nth Group X" — its group's last standing — is a slot guaranteed to be
  // occupied by the Bye no matter how the group stage plays out. If that slot advances
  // into this bracket, whichever Round 1 match pairs against it is a known walkover
  // before a single group match has been played.
  const byeGuaranteedLabels = new Set<string>();
  groups.forEach((group, g) => {
    const hasBye = (group.teamIds || []).some(tid =>
      (tournament.registeredTeams || []).some((t: any) => t.id === tid && t.isBye)
    );
    if (hasBye && group.teamIds && group.teamIds.length > 0) {
      const lastPos = group.teamIds.length - 1;
      // Two different label formats reach this set depending on where the descriptor came
      // from: this file's own generators write "Nth Group X", but a descriptor pulled from
      // saved custom seeding (KnockoutSeedingPreview.tsx) writes the shorter "Nth X" — same
      // slot, no "Group" word. Register both so a custom-seeded bracket still gets detected.
      byeGuaranteedLabels.add(`${getPositionLabel(lastPos)} Group ${groupNames[g]}`);
      byeGuaranteedLabels.add(`${getPositionLabel(lastPos)} ${groupNames[g]}`);
    }
  });

  let teamDescriptions: string[] = [];

  // If custom seeding was set by the admin in the Knockout Seeding Preview,
  // use those descriptions directly — they are already in the correct interleaved
  // pair order (team1Match1, team2Match1, team1Match2, ...).
  const customSeeding =
    bracketType === 'mastersCup'
      ? tournament.customMastersCupSeeding
      : bracketType === 'tourCup'
      ? tournament.customTourCupSeeding
      : undefined;

  if (customSeeding && customSeeding.length > 0) {
    teamDescriptions = customSeeding;
  } else {
    // Build all team descriptions in rank order (strongest first, weakest last),
    // then cross-seed by pairing the top half against the reversed bottom half.
    // This mirrors the server seedTourCupTeams algorithm and handles any advanceCount.
    let allTeamDescriptions: string[] = [];
    if (tournament.mastersCupAdvancementRules?.length || tournament.tourCupAdvancementRules?.length) {
      const { mastersCup, tourCup } = buildAdvancementDescriptorLists(
        groups,
        groupNames,
        tournament.mastersCupAdvancementRules ?? [],
        tournament.tourCupAdvancementRules ?? [{ type: 'remaining' }]
      );
      allTeamDescriptions = bracketType === 'mastersCup' ? mastersCup : tourCup;
    } else if (bracketType === 'mastersCup') {
      for (let position = 0; position < mastersCupAdvanceCount; position++) {
        const posLabel = getPositionLabel(position);
        for (let g = 0; g < groupCount; g++) {
          allTeamDescriptions.push(`${posLabel} Group ${groupNames[g]}`);
        }
      }
    } else {
      // Tour Cup positions start right after Masters Cup positions
      for (let p = 0; p < tourCupAdvanceCount; p++) {
        const position = mastersCupAdvanceCount + p;
        const posLabel = getPositionLabel(position);
        for (let g = 0; g < groupCount; g++) {
          allTeamDescriptions.push(`${posLabel} Group ${groupNames[g]}`);
        }
      }
    }

    // Cross-seed: pair seed 1 vs seed N, seed 2 vs seed N-1, etc.
    const seedHalf = Math.floor(allTeamDescriptions.length / 2);
    const topHalf = allTeamDescriptions.slice(0, seedHalf);
    const bottomHalf = [...allTeamDescriptions.slice(seedHalf)].reverse();
    for (let i = 0; i < Math.max(topHalf.length, bottomHalf.length); i++) {
      if (i < topHalf.length) teamDescriptions.push(topHalf[i]);
      if (i < bottomHalf.length) teamDescriptions.push(bottomHalf[i]);
    }
  }
  
  // Generate bracket pairings (single elimination)
  const numTeams = teamDescriptions.length;
  const rounds = Math.ceil(Math.log2(numTeams));
  const placeholders: Array<{ team1Placeholder: string; team2Placeholder: string; round: string; previousMatch1?: number; previousMatch2?: number; isWalkover?: boolean }> = [];

  // First round pairings (no previous matches).
  const firstRoundMatches = Math.pow(2, rounds - 1);
  // numTeams isn't always a power of two (e.g. a leftover cross-group slot), so pad up to
  // a full Round 1 with literal byes — otherwise the last team(s) get silently dropped and
  // every subsequent round's match-index bookkeeping (below) shifts out of alignment.
  const paddedDescriptions = [...teamDescriptions];
  while (paddedDescriptions.length < firstRoundMatches * 2) paddedDescriptions.push('BYE');
  // Parallel to `placeholders`: what to display wherever a later match references "the
  // winner/loser of match i". Normally that's undecided until it's actually played
  // ("Winner of Match #N" / "Loser of Match #N"), named after that match's own #N header
  // so the reference is trivial to look up instead of requiring a separate A/B/C scheme.
  // A walkover has no real opponent, so both sides are already fully known: the non-BYE
  // side is a guaranteed winner, and 'BYE' itself propagates onward as "the loser" — which
  // can in turn make a *later* match (e.g. a consolation-semi fed by this one) a walkover
  // too, cascading exactly like it would in the real generated bracket.
  const winnerLabelByIndex: string[] = [];
  const loserLabelByIndex: string[] = [];

  // Appends a match, deriving isWalkover from its (possibly already-cascaded) team
  // placeholders and recording what later rounds should show for its winner/loser.
  const pushMatch = (
    team1Placeholder: string,
    team2Placeholder: string,
    round: string,
    previousMatch1?: number,
    previousMatch2?: number,
  ) => {
    const isWalkover = team1Placeholder === 'BYE' || team2Placeholder === 'BYE';
    const thisIndex = placeholders.length;
    placeholders.push({ team1Placeholder, team2Placeholder, round, previousMatch1, previousMatch2, isWalkover });
    winnerLabelByIndex.push(
      isWalkover ? (team1Placeholder === 'BYE' ? team2Placeholder : team1Placeholder)
        : `Winner of Match #${thisIndex + 1}`
    );
    loserLabelByIndex.push(isWalkover ? 'BYE' : `Loser of Match #${thisIndex + 1}`);
  };

  for (let i = 0; i < firstRoundMatches; i++) {
    const team1Placeholder = paddedDescriptions[i * 2] || 'TBD';
    const team2Placeholder = paddedDescriptions[i * 2 + 1] || 'TBD';
    // A group-bye-guaranteed slot (e.g. "3rd Group A") still displays its real descriptor
    // here — the round-1 card should show which slot it is, not the literal word BYE.
    const team1IsGuaranteedOut = team1Placeholder === 'BYE' || byeGuaranteedLabels.has(team1Placeholder);
    const team2IsGuaranteedOut = team2Placeholder === 'BYE' || byeGuaranteedLabels.has(team2Placeholder);
    const isWalkover = team1IsGuaranteedOut || team2IsGuaranteedOut;
    const thisIndex = placeholders.length;
    placeholders.push({ team1Placeholder, team2Placeholder, round: getRoundName(rounds, 1), isWalkover });
    winnerLabelByIndex.push(
      team1IsGuaranteedOut ? team2Placeholder
        : team2IsGuaranteedOut ? team1Placeholder
        : `Winner of Match #${thisIndex + 1}`
    );
    loserLabelByIndex.push(isWalkover ? 'BYE' : `Loser of Match #${thisIndex + 1}`);
  }

  // Subsequent rounds (winners advance)
  let matchIndex = firstRoundMatches; // Continue counting from first round
  for (let round = 2; round <= rounds; round++) {
    const roundMatches = Math.pow(2, rounds - round);
    for (let i = 0; i < roundMatches; i++) {
      // Calculate which previous matches feed into this one
      const prevMatch1Index = matchIndex - (roundMatches * 2) + (i * 2);
      const prevMatch2Index = prevMatch1Index + 1;

      pushMatch(
        winnerLabelByIndex[prevMatch1Index],
        winnerLabelByIndex[prevMatch2Index],
        getRoundName(rounds, round),
        prevMatch1Index,
        prevMatch2Index,
      );
      matchIndex++;
    }
  }

  // Add 3rd place match and consolation bracket if needed
  const playAllPositions = tournament.playAllPositions || false;

  if (playAllPositions && rounds > 1) {
    // 3rd place match (losers of semi-finals)
    const semiFinalIndices: number[] = [];
    for (let i = 0; i < placeholders.length; i++) {
      if (placeholders[i].round === 'semi-finals') {
        semiFinalIndices.push(i);
      }
    }

    if (semiFinalIndices.length >= 2) {
      pushMatch(
        loserLabelByIndex[semiFinalIndices[0]],
        loserLabelByIndex[semiFinalIndices[1]],
        'third-place',
        semiFinalIndices[0],
        semiFinalIndices[1],
      );
    }

    // If there were quarter-finals (rounds > 2), add 5th-8th place playoffs
    if (rounds > 2) {
      const quarterFinalIndices: number[] = [];
      for (let i = 0; i < placeholders.length; i++) {
        if (placeholders[i].round === 'quarter-finals') {
          quarterFinalIndices.push(i);
        }
      }

      if (quarterFinalIndices.length >= 4) {
        // Consolation semi-finals (for places 5-8)
        const consolationSemi1Index = placeholders.length;
        pushMatch(
          loserLabelByIndex[quarterFinalIndices[0]],
          loserLabelByIndex[quarterFinalIndices[1]],
          'consolation-semi-finals',
          quarterFinalIndices[0],
          quarterFinalIndices[1],
        );

        const consolationSemi2Index = placeholders.length;
        pushMatch(
          loserLabelByIndex[quarterFinalIndices[2]],
          loserLabelByIndex[quarterFinalIndices[3]],
          'consolation-semi-finals',
          quarterFinalIndices[2],
          quarterFinalIndices[3],
        );

        // 5th place match (winners of consolation semi-finals)
        pushMatch(
          winnerLabelByIndex[consolationSemi1Index],
          winnerLabelByIndex[consolationSemi2Index],
          'fifth-place',
          consolationSemi1Index,
          consolationSemi2Index,
        );

        // 7th place match (losers of consolation semi-finals)
        pushMatch(
          loserLabelByIndex[consolationSemi1Index],
          loserLabelByIndex[consolationSemi2Index],
          'seventh-place',
          consolationSemi1Index,
          consolationSemi2Index,
        );
      }
    }
  }

  return placeholders;
}

/**
 * Helper to get round name for knockout bracket
 */
function getRoundName(totalRounds: number, currentRound: number): string {
  const roundsFromEnd = totalRounds - currentRound;
  if (roundsFromEnd === 0) return 'final';
  if (roundsFromEnd === 1) return 'semi-finals';
  if (roundsFromEnd === 2) return 'quarter-finals';
  return `Round ${currentRound}`;
}

/**
 * Optimize match order to balance player waiting times
 * Uses a greedy algorithm: always schedule the match for which players have waited longest
 * Takes into account that multiple courts can host matches at the same time
 */
function optimizeMatchOrder(matches: ScheduledMatch[], tournament: Tournament): ScheduledMatch[] {
  const optimized: ScheduledMatch[] = [];
  const remaining = [...matches];
  const numberOfCourts = tournament.numberOfCourts || 4;
  
  // Track last scheduled slot index for each player
  const playerLastSlot = new Map<string, number>(); // playerId -> slotIndex
  
  // Helper to get player IDs from a match
  const getMatchPlayerIds = (match: ScheduledMatch): string[] => {
    if (!match.team1Id || !match.team2Id) return [];
    
    const team1 = tournament.registeredTeams?.find(t => t.id === match.team1Id);
    const team2 = tournament.registeredTeams?.find(t => t.id === match.team2Id);
    
    return [
      team1?.player1Id,
      team1?.player2Id,
      team2?.player1Id,
      team2?.player2Id
    ].filter(Boolean) as string[];
  };
  
  // Schedule matches one by one, always picking the one with longest player wait
  // IMPORTANT: currentSlot represents the sequential match number, not the time slot
  // We need to convert it to actual time slots by dividing by number of courts
  let currentSlot = 0;
  
  while (remaining.length > 0) {
    let bestMatchIndex = 0;
    let bestScore = -Infinity;
    
    // Calculate the current TIME slot (multiple matches can happen at same time on different courts)
    const currentTimeSlot = Math.floor(currentSlot / numberOfCourts);
    
    // Find the match where players have waited the longest
    for (let i = 0; i < remaining.length; i++) {
      const match = remaining[i];
      const playerIds = getMatchPlayerIds(match);
      
      if (playerIds.length === 0) {
        // No player info, just schedule it
        bestMatchIndex = i;
        break;
      }
      
      // Calculate score: sum of waiting time for all 4 players
      // Players who haven't played yet get a base score
      const waitingScore = playerIds.reduce((sum, playerId) => {
        const lastSlot = playerLastSlot.get(playerId);
        if (lastSlot === undefined) {
          // Player hasn't played yet - give moderate priority
          return sum + currentTimeSlot * 0.5;
        }
        // Player has played - prioritize if they've waited longer
        // Convert both to time slots for proper comparison
        const lastTimeSlot = Math.floor(lastSlot / numberOfCourts);
        const waitingTimeSlots = currentTimeSlot - lastTimeSlot;
        return sum + waitingTimeSlots;
      }, 0);
      
      if (waitingScore > bestScore) {
        bestScore = waitingScore;
        bestMatchIndex = i;
      }
    }
    
    // Schedule the best match
    const scheduledMatch = remaining.splice(bestMatchIndex, 1)[0];
    optimized.push(scheduledMatch);
    
    // Update last slot for all players in this match
    const playerIds = getMatchPlayerIds(scheduledMatch);
    playerIds.forEach(playerId => {
      playerLastSlot.set(playerId, currentSlot);
    });
    
    currentSlot++;
  }
  
  console.log(`Optimized ${optimized.length} matches with ${numberOfCourts} courts`);
  return optimized;
}

/**
 * Calculate expected knockout matches based on tournament format
 * Returns count of matches needed for each bracket type
 */
function calculateExpectedKnockoutMatches(tournament: Tournament): {
  mastersCup: number;
  tourCup: number;
} {
  if (tournament.format !== "groups-knockout") {
    return { mastersCup: 0, tourCup: 0 };
  }

  const groupCount = tournament.groups?.length || 0;
  const playAllPositions = tournament.playAllPositions || false;
  
  /**
   * Calculate total matches for a single-elimination bracket with optional consolation matches
   * @param numTeams - Number of teams in the bracket
   * @param includeConsolation - Whether to include consolation matches (3rd, 5th, 7th place)
   */
  const calculateBracketMatches = (numTeams: number, includeConsolation: boolean): number => {
    if (numTeams <= 0) return 0;
    
    const rounds = Math.ceil(Math.log2(numTeams));

    // Main bracket: generateKnockoutPlaceholders pads non-power-of-2 team counts up to the
    // next power of 2 and creates an explicit placeholder card for every padded (BYE) slot,
    // so the real entry count is 2^rounds - 1, not numTeams - 1 (they only coincide when
    // numTeams is already a power of 2). Undercounting here truncates the tail of the
    // placeholder array below via .slice(0, expectedKnockout.X) — silently dropping the
    // last-generated matches (fifth-place/seventh-place) whenever padding was needed.
    let totalMatches = Math.pow(2, rounds) - 1;
    
    if (includeConsolation && rounds > 1) {
      // 3rd place match (losers of semi-finals)
      totalMatches += 1;
      
      // If there were quarter-finals (rounds > 2), add 5th-8th place playoffs
      if (rounds > 2) {
        totalMatches += 2; // 2 consolation semi-finals
        totalMatches += 1; // 5th place match
        totalMatches += 1; // 7th place match
      }
    }
    
    return totalMatches;
  };
  
  if (tournament.knockoutFormatType === "double-bracket") {
    let mastersCupTeams: number;
    let tourCupTeams: number;

    if (tournament.mastersCupAdvancementRules?.length || tournament.tourCupAdvancementRules?.length) {
      // Rule-based advancement: count how many teams each rule contributes rather than
      // assuming an even split per group. 'remaining' takes whatever's left of the
      // tournament's total team pool after earlier rules (in either bracket) are counted.
      const totalTeams = (tournament.groups || []).reduce((sum, g) => sum + (g.teamIds?.length || 0), 0);
      let claimed = 0;
      const countRules = (rules: AdvancementRule[]): number =>
        rules.reduce((sum, rule) => {
          const count =
            rule.type === 'group-position' ? groupCount :
            rule.type === 'best-of-position-across-groups' ? rule.count :
            Math.max(0, totalTeams - claimed);
          claimed += count;
          return sum + count;
        }, 0);
      mastersCupTeams = countRules(tournament.mastersCupAdvancementRules ?? []);
      tourCupTeams = countRules(tournament.tourCupAdvancementRules ?? [{ type: 'remaining' }]);
    } else {
      // Masters Cup: top N from each group
      const mastersCupAdvance = tournament.mastersCupAdvanceCount || 2;
      mastersCupTeams = groupCount * mastersCupAdvance;

      // Tour Cup: next N from each group
      const tourCupAdvance = tournament.tourCupAdvanceCount || 2;
      tourCupTeams = groupCount * tourCupAdvance;
    }

    const mastersCupMatches = calculateBracketMatches(mastersCupTeams, playAllPositions);
    const tourCupMatches = calculateBracketMatches(tourCupTeams, playAllPositions);

    return { mastersCup: mastersCupMatches, tourCup: tourCupMatches };
  } else {
    // Single bracket
    const knockoutAdvance = tournament.knockoutAdvanceCount || 2;
    const knockoutTeams = groupCount * knockoutAdvance;
    const knockoutMatches = calculateBracketMatches(knockoutTeams, playAllPositions);
    
    return { mastersCup: knockoutMatches, tourCup: 0 };
  }
}

/**
 * Automatically schedule all matches (clears and reschedules everything)
 * 
 * IMPORTANT CONSTRAINTS:
 * 1. No player can play on multiple courts at the same time
 * 2. Each player is available maximum once per time slot
 * 3. If a player conflict is detected, the match is scheduled in the next available slot
 */
export function autoScheduleMatches(tournament: Tournament, seed: number = 0): Tournament {
  if (!tournament.startTime || !tournament.numberOfCourts) {
    console.warn("Cannot schedule: missing startTime or numberOfCourts");
    return tournament;
  }

  console.log("=== AUTO-SCHEDULING (RESCHEDULE ALL) ===");
  console.log("Match Duration:", tournament.timedMatchDuration || 20, "minutes");
  console.log("Changeover Time:", tournament.changeoverTime || 0, "minutes");
  console.log("Number of Courts:", tournament.numberOfCourts);
  
  // CLEAR ALL EXISTING SCHEDULES to allow rescheduling
  const cleanedTournament = JSON.parse(JSON.stringify(tournament)); // Deep clone
  
  // Clear group match schedules
  if (cleanedTournament.groups) {
    cleanedTournament.groups.forEach((group: any) => {
      if (group.matchResults) {
        group.matchResults.forEach((match: any) => {
          // Keep the match result scores but clear scheduling info
          match.startTime = undefined;
          match.courtNumber = undefined;
        });
      }
    });
  }
  
  // Clear knockout match schedules
  if (cleanedTournament.knockoutBracket?.mastersCup) {
    cleanedTournament.knockoutBracket.mastersCup.forEach((match: any) => {
      match.startTime = undefined;
      match.courtNumber = undefined;
    });
  }
  
  if (cleanedTournament.knockoutBracket?.tourCup) {
    cleanedTournament.knockoutBracket.tourCup.forEach((match: any) => {
      match.startTime = undefined;
      match.courtNumber = undefined;
    });
  }
  
  console.log("Cleared all existing schedules - starting fresh");

  const baseDate = tournament.startDate
    ? new Date(tournament.startDate)
    : new Date();

  // Generate available time slots
  const timeSlots = generateTimeSlots(tournament, baseDate);
  const availableSlots = timeSlots.filter((slot) => slot.available);
  
  // CRITICAL: Sort slots by time then by court to ensure chronological order
  // This guarantees that knockout matches are scheduled after all group matches
  availableSlots.sort((a, b) => {
    const timeA = new Date(a.startTime).getTime();
    const timeB = new Date(b.startTime).getTime();
    if (timeA !== timeB) return timeA - timeB;
    return a.courtNumber - b.courtNumber;
  });

  console.log("Generated", timeSlots.length, "total slots");
  console.log("Available slots:", availableSlots.length);
  
  // Show distribution of first few slots
  if (availableSlots.length > 0) {
    console.log("First 10 slots:");
    availableSlots.slice(0, 10).forEach((slot, i) => {
      const time = new Date(slot.startTime).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      console.log(`  Slot ${i + 1}: ${time} - Court ${slot.courtNumber}`);
    });
  }

  if (availableSlots.length === 0) {
    console.warn("No available time slots");
    return tournament;
  }

  // Get all matches (from cleaned tournament with no schedules)
  const matches = getAllMatches(cleanedTournament);
  console.log("=== SCHEDULE ALGORITHM DEBUG ===");
  console.log("Total matches found:", matches.length);
  if (matches.length > 0) {
    console.log("Sample match:", matches[0]);
    console.log("All match IDs:", matches.map(m => m.matchId));
  }
  
  // Separate group and knockout matches
  const groupMatches = matches.filter(m => m.type === "group");
  const knockoutMatches = matches.filter(m => m.type === "knockout");
  
  // Calculate expected knockout matches
  const expectedKnockout = calculateExpectedKnockoutMatches(cleanedTournament);
  const existingKnockout = {
    mastersCup: cleanedTournament.knockoutBracket?.mastersCup?.length || 0,
    tourCup: cleanedTournament.knockoutBracket?.tourCup?.length || 0,
  };

  const totalExpectedKnockout = expectedKnockout.mastersCup + expectedKnockout.tourCup;

  // If the existing bracket is entirely placeholders (real knockout not yet generated),
  // always clear and regenerate from scratch so stale or corrupted placeholder data is
  // never reused.  Placeholder arrays are just reserved time slots — they carry no
  // scores or real team assignments, so it is always safe to rebuild them.
  const allMastersCupArePlaceholders =
    existingKnockout.mastersCup > 0 &&
    (cleanedTournament.knockoutBracket?.mastersCup || []).every((m: any) => m.isPlaceholder);
  const allTourCupArePlaceholders =
    existingKnockout.tourCup > 0 &&
    (cleanedTournament.knockoutBracket?.tourCup || []).every((m: any) => m.isPlaceholder);

  if (allMastersCupArePlaceholders && cleanedTournament.knockoutBracket) {
    console.log("All Masters Cup entries are placeholders — clearing for fresh regeneration");
    cleanedTournament.knockoutBracket.mastersCup = undefined;
    existingKnockout.mastersCup = 0;
  }
  if (allTourCupArePlaceholders && cleanedTournament.knockoutBracket) {
    console.log("All Tour Cup entries are placeholders — clearing for fresh regeneration");
    cleanedTournament.knockoutBracket.tourCup = undefined;
    existingKnockout.tourCup = 0;
  }

  const hasKnockoutBracket = existingKnockout.mastersCup > 0 || existingKnockout.tourCup > 0;

  console.log("Knockout info:");
  console.log("  Expected Masters Cup matches:", expectedKnockout.mastersCup);
  console.log("  Existing Masters Cup matches:", existingKnockout.mastersCup);
  console.log("  Expected Tour Cup matches:", expectedKnockout.tourCup);
  console.log("  Existing Tour Cup matches:", existingKnockout.tourCup);
  console.log("  Total expected knockout slots:", totalExpectedKnockout);
  console.log("  Has knockout bracket:", hasKnockoutBracket);
  
  // SCHEDULING STRATEGY: Always schedule all matches
  let matchesToSchedule: ScheduledMatch[];
  let slotsToReserve = 0;
  
  if (!hasKnockoutBracket && totalExpectedKnockout > 0) {
    // Knockout hasn't been generated yet - only schedule group matches and reserve slots
    matchesToSchedule = groupMatches;
    slotsToReserve = totalExpectedKnockout;
    console.log(`Scheduling strategy: Group matches only (${groupMatches.length}), reserving ${slotsToReserve} slots for knockout`);
  } else {
    // Schedule all matches (knockout exists or no knockout format)
    matchesToSchedule = matches;
    console.log(`Scheduling strategy: All matches (${matches.length})`);
  }

  if (matchesToSchedule.length === 0) {
    console.warn("No matches to schedule");
    console.warn("Tournament structure:", {
      groupsCount: cleanedTournament.groups?.length || 0,
      groupsWithTeams: cleanedTournament.groups?.filter((g: any) => g.teamIds && g.teamIds.length > 0).length || 0,
      knockoutMastersCup: cleanedTournament.knockoutBracket?.mastersCup?.length || 0,
      knockoutTourCup: cleanedTournament.knockoutBracket?.tourCup?.length || 0,
    });
    return cleanedTournament; // Return cleaned tournament to reflect any changes made
  }

  // WAITING TIME OPTIMIZATION: For group stage matches, reorder to balance player waiting times
  if (!hasKnockoutBracket && matchesToSchedule.length > 0 && matchesToSchedule[0].type === "group") {
    console.log("=== OPTIMIZING FOR BALANCED WAITING TIMES ===");
    // Note: We still optimize order as a starting point, but the actual scheduling
    // will prioritize filling all courts at each time slot
    matchesToSchedule = optimizeMatchOrder(matchesToSchedule, cleanedTournament);
    console.log("Match order optimized for fairness");
  }

  // SEPARATE group and knockout matches to schedule them in order
  const groupMatchesToSchedule = matchesToSchedule.filter(m => m.type === "group");
  let knockoutMatchesToSchedule = matchesToSchedule.filter(m => m.type === "knockout");
  
  // INTERLEAVE KNOCKOUT MATCHES: Tour Cup then Masters Cup, ending with Masters Cup final
  if (knockoutMatchesToSchedule.length > 0) {
    console.log("\n=== INTERLEAVING KNOCKOUT ROUNDS ===");
    
    // Separate by bracket type
    const mastersCupMatches = knockoutMatchesToSchedule.filter(m => m.bracketType === "mastersCup");
    const tourCupMatches = knockoutMatchesToSchedule.filter(m => m.bracketType === "tourCup");
    
    console.log(`Masters Cup matches: ${mastersCupMatches.length}`);
    console.log(`Tour Cup matches: ${tourCupMatches.length}`);
    
    // Get knockout bracket data to determine round order
    const mastersCupBracket = cleanedTournament.knockoutBracket?.mastersCup || [];
    const tourCupBracket = cleanedTournament.knockoutBracket?.tourCup || [];
    
    // Collect all unique rounds from both brackets
    const allBracketRounds = new Set<string>();
    mastersCupBracket.forEach((m: any) => m.round && allBracketRounds.add(m.round));
    tourCupBracket.forEach((m: any) => m.round && allBracketRounds.add(m.round));
    
    // Define comprehensive round order (dynamically include Round 1, Round 2, etc.)
    const roundOrder: string[] = [];
    
    // Add numbered rounds first (Round 1, Round 2, round-1, round-2, etc.) in order
    const numberedRounds = Array.from(allBracketRounds)
      .filter(r => r.startsWith('Round ') || r.startsWith('round-'))
      .sort((a, b) => {
        const aNum = parseInt(a.replace(/Round |round-/, ''));
        const bNum = parseInt(b.replace(/Round |round-/, ''));
        return aNum - bNum;
      });
    roundOrder.push(...numberedRounds);
    
    // Then add named knockout rounds (in logical play order)
    // Consolation matches are scheduled based on when teams are known:
    // - After QF: consolation-semi-finals (losers of QF)
    // - After SF: third-place (losers of SF) and final (winners of SF)
    // - After consolation-SF: seventh-place (losers) and fifth-place (winners)
    const namedRounds = [
      "round-of-32", 
      "round-of-16", 
      "quarter-finals",           // Tour Cup QF, then Masters Cup QF
      "consolation-semi-finals",  // Tour Cup consolation SF, then Masters Cup consolation SF
      "semi-finals",              // Tour Cup SF, then Masters Cup SF
      "seventh-place",            // Tour Cup 7th, then Masters Cup 7th
      "fifth-place",              // Tour Cup 5th, then Masters Cup 5th
      "third-place",              // Tour Cup 3rd, then Masters Cup 3rd
      "final"                     // Tour Cup Final, then Masters Cup Final
    ];
    namedRounds.forEach(round => {
      if (allBracketRounds.has(round)) {
        roundOrder.push(round);
      }
    });
    
    console.log('Detected rounds in brackets:', Array.from(allBracketRounds));
    console.log('Round order for scheduling:', roundOrder);
    
    // Group matches by round
    const groupByRound = (matches: ScheduledMatch[], bracketType: string) => {
      const byRound = new Map<string, ScheduledMatch[]>();
      const bracket = bracketType === "mastersCup" ? mastersCupBracket : tourCupBracket;
      
      matches.forEach(match => {
        // Get round from the actual bracket
        let round = "";
        if (match.matchIndex !== undefined && bracket[match.matchIndex]) {
          round = bracket[match.matchIndex].round || "";
        }
        
        if (round) {
          if (!byRound.has(round)) {
            byRound.set(round, []);
          }
          byRound.get(round)!.push(match);
        } else {
          console.warn(`⚠️  Match ${match.matchId} has no round! matchIndex: ${match.matchIndex}, bracket length: ${bracket.length}`);
          // DEBUG: Log the actual match data
          if (match.matchIndex !== undefined && bracket[match.matchIndex]) {
            console.warn(`    Match data:`, JSON.stringify(bracket[match.matchIndex]));
          }
        }
      });
      
      console.log(`  ${bracketType} rounds found:`, Array.from(byRound.keys()));
      // DEBUG: Log match distribution per round
      byRound.forEach((matches, round) => {
        console.log(`    ${round}: ${matches.length} matches, indices: [${matches.map(m => m.matchIndex).join(',')}]`);
      });
      return byRound;
    };
    
    const mastersByRound = groupByRound(mastersCupMatches, "mastersCup");
    const tourByRound = groupByRound(tourCupMatches, "tourCup");
    
    // Build interleaved array: Tour Cup Round X, then Masters Cup Round X
    const interleavedKnockout: ScheduledMatch[] = [];
    
    console.log('  Round order being used:', roundOrder);
    console.log('  Tour Cup rounds available:', Array.from(tourByRound.keys()));
    console.log('  Masters Cup rounds available:', Array.from(mastersByRound.keys()));
    
    roundOrder.forEach(round => {
      // Add Tour Cup matches for this round first
      if (tourByRound.has(round)) {
        const roundMatches = tourByRound.get(round)!;
        console.log(`  Adding ${roundMatches.length} Tour Cup ${round} matches`);
        interleavedKnockout.push(...roundMatches);
      }
      
      // Then add Masters Cup matches for this round
      if (mastersByRound.has(round)) {
        const roundMatches = mastersByRound.get(round)!;
        console.log(`  Adding ${roundMatches.length} Masters Cup ${round} matches`);
        interleavedKnockout.push(...roundMatches);
      }
    });
    
    // Check for any unprocessed rounds (safety check)
    const processedRounds = new Set(roundOrder);
    const allMatchRounds = new Set([...Array.from(tourByRound.keys()), ...Array.from(mastersByRound.keys())]);
    allMatchRounds.forEach(round => {
      if (!processedRounds.has(round)) {
        console.warn(`⚠️  Unrecognized round "${round}" - adding to end of schedule`);
        if (tourByRound.has(round)) {
          interleavedKnockout.push(...tourByRound.get(round)!);
        }
        if (mastersByRound.has(round)) {
          interleavedKnockout.push(...mastersByRound.get(round)!);
        }
      }
    });
    
    console.log(`Total knockout matches after interleaving: ${interleavedKnockout.length} (original: ${knockoutMatchesToSchedule.length})`);
    if (interleavedKnockout.length !== knockoutMatchesToSchedule.length) {
      console.error(`❌ MATCH COUNT MISMATCH! Lost ${knockoutMatchesToSchedule.length - interleavedKnockout.length} matches during interleaving!`);
    }
    
    // Replace the knockout matches array with the interleaved version
    knockoutMatchesToSchedule = interleavedKnockout;
  }
  
  console.log("=== COURT-FILLING SCHEDULING ALGORITHM ===");
  console.log(`Group matches: ${groupMatchesToSchedule.length}`);
  console.log(`Knockout matches: ${knockoutMatchesToSchedule.length}`);
  console.log(`Total courts: ${cleanedTournament.numberOfCourts}`);

  // Helper function to get player IDs from a team
  const getPlayerIdsFromTeam = (teamId: string): string[] => {
    const team = cleanedTournament.registeredTeams?.find((t: any) => t.id === teamId);
    if (!team) return [];
    const playerIds = [];
    if (team.player1Id) playerIds.push(team.player1Id);
    if (team.player2Id) playerIds.push(team.player2Id);
    return playerIds;
  };
  
  // Helper function to check if match has player conflict with already scheduled matches at this time
  const hasPlayerConflict = (playerIds: string[], playersInSlot: Set<string>): boolean => {
    return playerIds.some(playerId => playersInSlot.has(playerId));
  };
  
  // Track scheduled matches
  const scheduledMatches: ScheduledMatch[] = [];
  
  // Track last time slot where each player played
  const playerLastTimeSlot = new Map<string, number>();
  
  // Track last time slot where each team played (to spread matches out)
  const teamLastTimeSlot = new Map<string, number>();
  
  // Track how many matches each team has scheduled (for balanced distribution)
  const teamMatchCount = new Map<string, number>();
  
  // Deterministic per-match jitter based on seed — breaks score ties differently on each attempt.
  // Range is 0–0.49 so it never overrides a genuine score difference (scores differ by ≥1 when
  // match counts or wait slots differ at all).
  const matchJitter = (matchId: string): number => {
    let h = seed * 9301;
    for (let i = 0; i < matchId.length; i++) {
      h = (h * 31 + matchId.charCodeAt(i)) & 0x7fffffff;
    }
    return (h % 490) / 1000; // 0.000 – 0.489
  };

  // Score a candidate match for scheduling at the current time slot.
  // Goal: keep all teams progressing at the same pace (balance) AND equalize
  // waiting times so no team waits far longer or shorter than others.
  const getWaitingScore = (match: ScheduledMatch, currentTimeSlot: number): number => {
    if (!match.team1Id || !match.team2Id) return 0;

    const team1MatchCount = teamMatchCount.get(match.team1Id) || 0;
    const team2MatchCount = teamMatchCount.get(match.team2Id) || 0;

    // Balance: teams with fewer matches get higher priority so all teams progress evenly
    const avgMatchCount = (team1MatchCount + team2MatchCount) / 2;
    const balanceScore = 100 - avgMatchCount * 20;

    const team1LastSlot = teamLastTimeSlot.get(match.team1Id);
    const team2LastSlot = teamLastTimeSlot.get(match.team2Id);
    const team1Wait = team1LastSlot !== undefined ? currentTimeSlot - team1LastSlot : currentTimeSlot;
    const team2Wait = team2LastSlot !== undefined ? currentTimeSlot - team2LastSlot : currentTimeSlot;

    // Use the MINIMUM of the two teams' waits: this naturally equalizes breaks because
    // a match where one team just played will score low, keeping that team's rest time
    // growing until it catches up with the other teams.
    const waitEqualizationScore = Math.min(team1Wait, team2Wait);

    // Tiny jitter breaks ties differently for each seed without affecting genuine differences
    return balanceScore + waitEqualizationScore * 2 + matchJitter(match.matchId);
  };
  
  // Group slots by time
  const slotsByTime = new Map<string, typeof availableSlots>();
  availableSlots.forEach(slot => {
    const time = slot.startTime;
    if (!slotsByTime.has(time)) {
      slotsByTime.set(time, []);
    }
    slotsByTime.get(time)!.push(slot);
  });
  
  const uniqueTimeSlotsCount = slotsByTime.size;
  console.log(`Found ${uniqueTimeSlotsCount} unique time slots`);
  
  let currentTimeSlotIndex = 0;
  
  // PHASE 1: Schedule all group matches first
  console.log("\n=== PHASE 1: SCHEDULING GROUP MATCHES ===");
  let remainingMatches = [...groupMatchesToSchedule];
  
  for (const [timeKey, slotsAtThisTime] of Array.from(slotsByTime.entries())) {
    if (remainingMatches.length === 0) {
      console.log("All group matches scheduled!");
      break;
    }
    
    const courtsAvailable = slotsAtThisTime.length;
    const playersInThisSlot = new Set<string>();
    let matchesScheduledInSlot = 0;

    console.log(`\n--- Time Slot ${currentTimeSlotIndex} (${new Date(timeKey).toLocaleTimeString()}) - ${courtsAvailable} courts available ---`);

    // Try to fill all courts at this time slot
    for (let courtIdx = 0; courtIdx < courtsAvailable; courtIdx++) {
      if (remainingMatches.length === 0) break;

      // Pick the highest-scoring match that has no player conflict
      let bestMatchIndex = -1;
      let bestScore = -Infinity;

      for (let i = 0; i < remainingMatches.length; i++) {
        const match = remainingMatches[i];

        if (!match.team1Id || !match.team2Id) {
          bestMatchIndex = i;
          break;
        }

        const matchPlayerIds = [
          ...getPlayerIdsFromTeam(match.team1Id),
          ...getPlayerIdsFromTeam(match.team2Id),
        ];

        if (hasPlayerConflict(matchPlayerIds, playersInThisSlot)) continue;

        const score = getWaitingScore(match, currentTimeSlotIndex);
        if (score > bestScore) {
          bestScore = score;
          bestMatchIndex = i;
        }
      }
      
      if (bestMatchIndex === -1) {
        console.log(`  Court ${courtIdx + 1}: No valid matches (all have conflicts)`);
        break; // No more matches can be scheduled at this time
      }
      
      // Schedule the best match
      const match = remainingMatches.splice(bestMatchIndex, 1)[0];
      const slot = slotsAtThisTime[courtIdx];
      
      match.startTime = slot.startTime;
      match.courtNumber = slot.courtNumber;
      scheduledMatches.push(match);
      
      // Mark players as playing and update team last played time
      if (match.team1Id && match.team2Id) {
        const matchPlayerIds = [
          ...getPlayerIdsFromTeam(match.team1Id),
          ...getPlayerIdsFromTeam(match.team2Id)
        ];
        matchPlayerIds.forEach(id => {
          playersInThisSlot.add(id);
          playerLastTimeSlot.set(id, currentTimeSlotIndex);
        });
        
        // Update team last played time to spread matches
        const prevT1Slot = teamLastTimeSlot.get(match.team1Id);
        const prevT2Slot = teamLastTimeSlot.get(match.team2Id);
        
        teamLastTimeSlot.set(match.team1Id, currentTimeSlotIndex);
        teamLastTimeSlot.set(match.team2Id, currentTimeSlotIndex);
        
        // Increment match counts for balance tracking
        teamMatchCount.set(match.team1Id, (teamMatchCount.get(match.team1Id) || 0) + 1);
        teamMatchCount.set(match.team2Id, (teamMatchCount.get(match.team2Id) || 0) + 1);
        
        const t1Rest = prevT1Slot !== undefined ? currentTimeSlotIndex - prevT1Slot : "first";
        const t2Rest = prevT2Slot !== undefined ? currentTimeSlotIndex - prevT2Slot : "first";
        const t1Count = teamMatchCount.get(match.team1Id) || 0;
        const t2Count = teamMatchCount.get(match.team2Id) || 0;
        
        console.log(`  Court ${slot.courtNumber}: ${match.matchId} scheduled (score: ${bestScore.toFixed(1)}, T1: ${t1Count}matches/${t1Rest}rest, T2: ${t2Count}matches/${t2Rest}rest)`);
      } else {
        console.log(`  Court ${slot.courtNumber}: ${match.matchId} scheduled (score: ${bestScore.toFixed(1)})`);
      }
      
      matchesScheduledInSlot++;
    }
    
    console.log(`  Total: ${matchesScheduledInSlot}/${courtsAvailable} courts filled`);
    currentTimeSlotIndex++;
  }
  
  console.log(`\nGroup matches scheduled: ${groupMatchesToSchedule.length - remainingMatches.length}/${groupMatchesToSchedule.length}`);
  if (remainingMatches.length > 0) {
    console.warn(`WARNING: ${remainingMatches.length} group matches could not be scheduled!`);
  }
  
  // Find the latest time slot used by any group match
  let latestGroupMatchTimeSlot = -1;
  scheduledMatches.forEach(match => {
    if (match.type === "group" && match.startTime) {
      const matchTimeSlotIdx = Array.from(slotsByTime.keys()).findIndex(time => time === match.startTime);
      if (matchTimeSlotIdx > latestGroupMatchTimeSlot) {
        latestGroupMatchTimeSlot = matchTimeSlotIdx;
      }
    }
  });
  
  console.log(`Latest group match time slot: ${latestGroupMatchTimeSlot}`);
  
  // PHASE 2: Schedule knockout matches starting AFTER all group matches
  if (knockoutMatchesToSchedule.length > 0) {
    console.log("\n=== PHASE 2: SCHEDULING KNOCKOUT MATCHES ===");
    let remainingMatches = [...knockoutMatchesToSchedule];
    
    // Log details about the matches we're about to schedule
    console.log("Knockout matches to schedule:");
    remainingMatches.forEach((m, idx) => {
      const bracket = cleanedTournament.knockoutBracket?.[m.bracketType!] || [];
      const matchData = m.matchIndex !== undefined ? bracket[m.matchIndex] : null;
      const round = matchData?.round || "unknown";
      const hasTeams = m.team1Id && m.team2Id ? "✓" : "✗";
      console.log(`  ${idx + 1}. ${m.matchId} - ${round} - Teams: ${hasTeams}`);
    });
    
    // Continue from current time slot index, but ensure it's at least after the last group match
    const timeSlotEntries = Array.from(slotsByTime.entries());
    const minStartIdx = Math.max(currentTimeSlotIndex, latestGroupMatchTimeSlot + 1);
    console.log(`Starting Phase 2 from time slot index ${minStartIdx} of ${timeSlotEntries.length} total slots (ensuring after all group matches)`);
    
    // Use the minimum start index (after all group matches)
    let startIdx = minStartIdx;
    if (startIdx >= timeSlotEntries.length) {
      console.warn(`⚠️  Not enough time slots for knockout matches! Need more time or courts.`);
      // Don't schedule knockout matches if there's no room
      remainingMatches = [];
    }
    
    // Track which slots have been used (time + court combination)
    const usedSlots = new Set<string>();
    scheduledMatches.forEach(m => {
      if (m.startTime && m.courtNumber) {
        usedSlots.add(`${m.startTime}-${m.courtNumber}`);
      }
    });
    console.log(`${usedSlots.size} court slots already used by group matches`);
    
    for (let tsIdx = startIdx; tsIdx < timeSlotEntries.length; tsIdx++) {
      if (remainingMatches.length === 0) {
        console.log("All knockout matches scheduled!");
        break;
      }
      
      const [timeKey, slotsAtThisTime] = timeSlotEntries[tsIdx];
      const courtsAvailable = slotsAtThisTime.length;
      const playersInThisSlot = new Set<string>();
      let matchesScheduledInSlot = 0;
      
      console.log(`\n--- Time Slot ${tsIdx} (${new Date(timeKey).toLocaleTimeString()}) - ${courtsAvailable} courts available ---`);

      // Try to fill all courts at this time slot
      for (let courtIdx = 0; courtIdx < courtsAvailable; courtIdx++) {
        if (remainingMatches.length === 0) break;

        // Knockout matches are pre-sorted by round (interleaved Tour Cup + Masters Cup);
        // take the first valid match to preserve round sequence.
        let bestMatchIndex = -1;

        for (let i = 0; i < remainingMatches.length; i++) {
          const match = remainingMatches[i];

          if (!match.team1Id || !match.team2Id) {
            bestMatchIndex = i;
            break;
          }

          const matchPlayerIds = [
            ...getPlayerIdsFromTeam(match.team1Id),
            ...getPlayerIdsFromTeam(match.team2Id),
          ];

          if (hasPlayerConflict(matchPlayerIds, playersInThisSlot)) continue;

          bestMatchIndex = i;
          break;
        }
        
        if (bestMatchIndex === -1) {
          console.log(`  Court ${courtIdx + 1}: No valid matches (all have conflicts)`);
          break; // No more matches can be scheduled at this time
        }
        
        // Schedule the best match
        const match = remainingMatches.splice(bestMatchIndex, 1)[0];
        const slot = slotsAtThisTime[courtIdx];
        
        // Check if this slot was already used by Phase 1
        const slotKey = `${slot.startTime}-${slot.courtNumber}`;
        if (usedSlots.has(slotKey)) {
          console.log(`  ⚠️  Slot ${slotKey} already used, skipping this court`);
          // Put the match back since we couldn't schedule it
          remainingMatches.unshift(match);
          continue;
        }
        
        match.startTime = slot.startTime;
        match.courtNumber = slot.courtNumber;
        scheduledMatches.push(match);
        usedSlots.add(slotKey); // Mark this slot as used
        
        // Mark players as playing and update team last played time
        if (match.team1Id && match.team2Id) {
          const matchPlayerIds = [
            ...getPlayerIdsFromTeam(match.team1Id),
            ...getPlayerIdsFromTeam(match.team2Id)
          ];
          matchPlayerIds.forEach(id => {
            playersInThisSlot.add(id);
            playerLastTimeSlot.set(id, tsIdx);
          });
          
          const prevT1Slot = teamLastTimeSlot.get(match.team1Id);
          const prevT2Slot = teamLastTimeSlot.get(match.team2Id);
          
          teamLastTimeSlot.set(match.team1Id, tsIdx);
          teamLastTimeSlot.set(match.team2Id, tsIdx);
          
          teamMatchCount.set(match.team1Id, (teamMatchCount.get(match.team1Id) || 0) + 1);
          teamMatchCount.set(match.team2Id, (teamMatchCount.get(match.team2Id) || 0) + 1);
          
          const t1Rest = prevT1Slot !== undefined ? tsIdx - prevT1Slot : "first";
          const t2Rest = prevT2Slot !== undefined ? tsIdx - prevT2Slot : "first";
          
          console.log(`  Court ${slot.courtNumber}: ${match.matchId} scheduled (T1 rest: ${t1Rest}, T2 rest: ${t2Rest})`);
        } else {
          console.log(`  Court ${slot.courtNumber}: ${match.matchId} scheduled`);
        }
        
        matchesScheduledInSlot++;
      }
      
      console.log(`  Total: ${matchesScheduledInSlot}/${courtsAvailable} courts filled`);
    }
    
    console.log(`\nKnockout matches scheduled: ${knockoutMatchesToSchedule.length - remainingMatches.length}/${knockoutMatchesToSchedule.length}`);
    if (remainingMatches.length > 0) {
      console.warn(`WARNING: ${remainingMatches.length} knockout matches could not be scheduled!`);
    }
  }
  
  console.log(`\n=== SCHEDULING COMPLETE ===`);
  console.log(`Total scheduled: ${scheduledMatches.length} matches`);
  const totalMatchesToSchedule = groupMatchesToSchedule.length + knockoutMatchesToSchedule.length;
  console.log(`Expected: ${totalMatchesToSchedule} matches`);
  
  // Analyze balance: calculate rest times for each team
  console.log(`\n=== BALANCE ANALYSIS ===`);
  const teamSchedule = new Map<string, number[]>(); // teamId -> array of time slot indices
  
  scheduledMatches.forEach((match, idx) => {
    if (match.team1Id && match.team2Id) {
      // Find the time slot index for this match
      const matchTime = match.startTime;
      const timeSlotIdx = Array.from(slotsByTime.keys()).findIndex(time => time === matchTime);
      
      if (timeSlotIdx !== -1) {
        if (!teamSchedule.has(match.team1Id)) teamSchedule.set(match.team1Id, []);
        if (!teamSchedule.has(match.team2Id)) teamSchedule.set(match.team2Id, []);
        
        teamSchedule.get(match.team1Id)!.push(timeSlotIdx);
        teamSchedule.get(match.team2Id)!.push(timeSlotIdx);
      }
    }
  });
  
  // Calculate and display rest statistics for each team
  teamSchedule.forEach((timeSlots, teamId) => {
    if (timeSlots.length < 2) return; // Skip teams with only 1 match
    
    const sortedSlots = [...timeSlots].sort((a, b) => a - b);
    const breaks: number[] = [];
    
    for (let i = 1; i < sortedSlots.length; i++) {
      breaks.push(sortedSlots[i] - sortedSlots[i - 1]);
    }
    
    const minBreak = Math.min(...breaks);
    const maxBreak = Math.max(...breaks);
    const avgBreak = breaks.reduce((a, b) => a + b, 0) / breaks.length;
    
    // Get team name for logging
    const teamName = teamId.substring(0, 12) + "..."; // Truncate for readability
    
    console.log(`  ${teamName}: ${sortedSlots.length} matches | Breaks: min=${minBreak}, max=${maxBreak}, avg=${avgBreak.toFixed(1)} slots`);
  });
  
  // Now update the tournament structure with scheduled matches
  scheduledMatches.forEach(match => {
    // Update the match in the tournament structure
    if (match.type === "group" && match.groupIndex !== undefined && match.matchIndex !== undefined) {
      if (cleanedTournament.groups && cleanedTournament.groups[match.groupIndex]) {
        const group = cleanedTournament.groups[match.groupIndex];
        
        // Initialize matchResults array if it doesn't exist
        if (!group.matchResults) {
          group.matchResults = [];
        }
        
        // Check if this match already exists in matchResults
        const existingMatchIndex = group.matchResults.findIndex(
          (m: any) =>
            (m.team1Id === match.team1Id && m.team2Id === match.team2Id) ||
            (m.team1Id === match.team2Id && m.team2Id === match.team1Id)
        );
        
        if (existingMatchIndex >= 0) {
          // Update existing match - only update scheduling info, preserve scores
          group.matchResults[existingMatchIndex].startTime = match.startTime;
          group.matchResults[existingMatchIndex].courtNumber = match.courtNumber;
        } else {
          // Create new match result entry with scheduling info and default 0-0 scores
          group.matchResults.push({
            team1Id: match.team1Id!,
            team2Id: match.team2Id!,
            team1Games: 0,
            team2Games: 0,
            startTime: match.startTime,
            courtNumber: match.courtNumber,
          });
        }
      }
    } else if (match.type === "knockout" && match.bracketType && match.matchIndex !== undefined) {
      if (cleanedTournament.knockoutBracket?.[match.bracketType]) {
        const bracket = cleanedTournament.knockoutBracket[match.bracketType];
        if (bracket[match.matchIndex]) {
          bracket[match.matchIndex].startTime = match.startTime;
          bracket[match.matchIndex].courtNumber = match.courtNumber;
        }
      }
    }
  });

  // If knockout hasn't been generated yet, create placeholder matches with reserved slots
  if (!hasKnockoutBracket && totalExpectedKnockout > 0 && slotsToReserve > 0) {
    console.log(`\n=== CREATING KNOCKOUT PLACEHOLDERS ===`);
    console.log(`Creating ${totalExpectedKnockout} placeholder knockout matches`);
    
    // Find the latest time slot used by any group match
    let latestGroupMatchTime: string | null = null;
    let latestGroupMatchTimeSlotIdx = -1;
    scheduledMatches.forEach(match => {
      if (match.type === "group" && match.startTime) {
        const matchTimeSlotIdx = Array.from(slotsByTime.keys()).findIndex(time => time === match.startTime);
        if (matchTimeSlotIdx > latestGroupMatchTimeSlotIdx) {
          latestGroupMatchTimeSlotIdx = matchTimeSlotIdx;
          latestGroupMatchTime = match.startTime;
        }
      }
    });
    
    console.log(`Latest group match at time slot ${latestGroupMatchTimeSlotIdx} (${latestGroupMatchTime ? new Date(latestGroupMatchTime).toLocaleTimeString() : 'N/A'})`);
    
    // Find remaining available slots AFTER all group matches
    const usedSlots = new Set(scheduledMatches.map(m => `${m.startTime}-${m.courtNumber}`));
    const remainingSlots = availableSlots.filter(slot => {
      // Must not be used
      if (usedSlots.has(`${slot.startTime}-${slot.courtNumber}`)) {
        return false;
      }
      // Must be after the latest group match
      if (latestGroupMatchTime) {
        const slotTime = new Date(slot.startTime).getTime();
        const latestTime = new Date(latestGroupMatchTime).getTime();
        return slotTime > latestTime;
      }
      return true;
    });
    
    // IMPORTANT: Sort remaining slots by time to ensure chronological order
    remainingSlots.sort((a, b) => {
      const timeA = new Date(a.startTime).getTime();
      const timeB = new Date(b.startTime).getTime();
      if (timeA !== timeB) return timeA - timeB;
      // If same time, sort by court number for consistency
      return a.courtNumber - b.courtNumber;
    });
    
    console.log(`Found ${remainingSlots.length} available slots for knockout placeholders (after all group matches)`);
    if (remainingSlots.length > 0) {
      console.log(`First knockout placeholder slot: ${new Date(remainingSlots[0].startTime).toLocaleTimeString()} on Court ${remainingSlots[0].courtNumber}`);
    }
    
    let remainingSlotIndex = 0;
    
    // Initialize knockout bracket structure if it doesn't exist
    if (!cleanedTournament.knockoutBracket) {
      cleanedTournament.knockoutBracket = {};
    }

    // Build placeholder descriptions for both brackets, then interleave by round
    // (Tour Cup round N first, Masters Cup round N second) before assigning time slots.
    // This ensures the schedule order matches the desired round-interleaved layout.

    const KNOCKOUT_ROUND_ORDER = [
      'Round 1','Round 2','Round 3','Round 4','Round 5',
      'round-of-32','round-of-16','quarter-finals',
      'consolation-semi-finals','semi-finals',
      'seventh-place','fifth-place','third-place','final',
    ];

    type PlaceholderDesc = {
      team1Placeholder: string;
      team2Placeholder: string;
      round: string;
      previousMatch1?: number;
      previousMatch2?: number;
      isWalkover?: boolean;
    };

    const mcDescs: PlaceholderDesc[] = expectedKnockout.mastersCup > 0
      ? generateKnockoutPlaceholders(tournament, 'mastersCup', expectedKnockout.mastersCup)
      : [];
    const tcDescs: PlaceholderDesc[] = expectedKnockout.tourCup > 0
      ? generateKnockoutPlaceholders(tournament, 'tourCup', expectedKnockout.tourCup)
      : [];

    // Collect all unique rounds present in either bracket
    const allRounds = new Set([...mcDescs.map(d => d.round), ...tcDescs.map(d => d.round)]);
    const numberedRounds = Array.from(allRounds)
      .filter(r => r.startsWith('Round '))
      .sort((a, b) => parseInt(a.split(' ')[1]) - parseInt(b.split(' ')[1]));
    const namedRounds = KNOCKOUT_ROUND_ORDER.filter(r => !r.startsWith('Round ') && allRounds.has(r));
    const sortedRounds = [...numberedRounds, ...namedRounds];
    Array.from(allRounds).forEach(r => { if (!sortedRounds.includes(r)) sortedRounds.push(r); });

    // Group each bracket's descriptions by round (preserving insertion order within each round)
    const mcByRound = new Map<string, PlaceholderDesc[]>();
    const tcByRound = new Map<string, PlaceholderDesc[]>();
    mcDescs.forEach(d => { if (!mcByRound.has(d.round)) mcByRound.set(d.round, []); mcByRound.get(d.round)!.push(d); });
    tcDescs.forEach(d => { if (!tcByRound.has(d.round)) tcByRound.set(d.round, []); tcByRound.get(d.round)!.push(d); });

    // Interleaved assignment: Tour Cup round N, then Masters Cup round N
    // We track separate slot indices for MC and TC after assignment
    type TaggedDesc = PlaceholderDesc & { bracket: 'mastersCup' | 'tourCup'; origIndex: number };
    const interleaved: TaggedDesc[] = [];
    sortedRounds.forEach(round => {
      (tcByRound.get(round) || []).forEach((d, i) => interleaved.push({ ...d, bracket: 'tourCup', origIndex: tcDescs.indexOf(d) }));
      (mcByRound.get(round) || []).forEach((d, i) => interleaved.push({ ...d, bracket: 'mastersCup', origIndex: mcDescs.indexOf(d) }));
    });

    // Assign slots in interleaved order, recording startTime/courtNumber per origIndex per bracket.
    // Slots flagged isWalkover are a guaranteed Bye match (known from the group draw alone) —
    // skip reserving a slot for them entirely so that court/time is freed up for real matches.
    const mcSlots = new Map<number, { startTime: string; courtNumber: number }>();
    const tcSlots = new Map<number, { startTime: string; courtNumber: number }>();
    interleaved.forEach(item => {
      if (item.isWalkover) return;
      if (remainingSlotIndex < remainingSlots.length) {
        const slot = remainingSlots[remainingSlotIndex++];
        if (item.bracket === 'mastersCup') mcSlots.set(item.origIndex, slot);
        else tcSlots.set(item.origIndex, slot);
      }
    });

    // Build final placeholder arrays in their original match order (for correct bracket linkage)
    if (expectedKnockout.mastersCup > 0 && mcDescs.length > 0) {
      const placeholders = mcDescs.slice(0, expectedKnockout.mastersCup).map((desc, i) => ({
        id: `placeholder-masters-${i}`,
        round: desc.round,
        matchNumber: i,
        knockoutType: 'masters-cup' as const,
        startTime: mcSlots.get(i)?.startTime,
        courtNumber: mcSlots.get(i)?.courtNumber,
        isPlaceholder: true,
        isWalkover: desc.isWalkover || undefined,
        team1Placeholder: desc.team1Placeholder,
        team2Placeholder: desc.team2Placeholder,
        previousMatch1: desc.previousMatch1,
        previousMatch2: desc.previousMatch2,
      }));
      cleanedTournament.knockoutBracket.mastersCup = placeholders;
    }

    if (expectedKnockout.tourCup > 0 && tcDescs.length > 0) {
      const placeholders = tcDescs.slice(0, expectedKnockout.tourCup).map((desc, i) => ({
        id: `placeholder-tour-${i}`,
        round: desc.round,
        matchNumber: i,
        knockoutType: 'tour-cup' as const,
        startTime: tcSlots.get(i)?.startTime,
        courtNumber: tcSlots.get(i)?.courtNumber,
        isPlaceholder: true,
        isWalkover: desc.isWalkover || undefined,
        team1Placeholder: desc.team1Placeholder,
        team2Placeholder: desc.team2Placeholder,
        previousMatch1: desc.previousMatch1,
        previousMatch2: desc.previousMatch2,
      }));
      cleanedTournament.knockoutBracket.tourCup = placeholders;
    }
  }

  return cleanedTournament;
}

/**
 * Validate that all matches fit within the available time
 */
export function validateSchedule(tournament: Tournament): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (!tournament.startTime) {
    errors.push("Tournament start time is not set");
  }

  if (!tournament.numberOfCourts || tournament.numberOfCourts < 1) {
    errors.push("Number of courts must be at least 1");
  }

  const matches = getAllMatches(tournament);
  const scheduledMatches = matches.filter(
    (m) => m.startTime && m.courtNumber > 0,
  );

  if (scheduledMatches.length < matches.length) {
    errors.push(
      `Only ${scheduledMatches.length} of ${matches.length} matches are scheduled`,
    );
  }

  // Check for time conflicts (two matches on same court at same time)
  for (let i = 0; i < scheduledMatches.length; i++) {
    for (let j = i + 1; j < scheduledMatches.length; j++) {
      const match1 = scheduledMatches[i];
      const match2 = scheduledMatches[j];

      if (match1.courtNumber === match2.courtNumber) {
        const start1 = new Date(match1.startTime).getTime();
        const end1 = start1 + match1.duration * 60000;
        const start2 = new Date(match2.startTime).getTime();
        const end2 = start2 + match2.duration * 60000;

        if (
          (start1 >= start2 && start1 < end2) ||
          (end1 > start2 && end1 <= end2) ||
          (start1 <= start2 && end1 >= end2)
        ) {
          errors.push(
            `Time conflict on court ${match1.courtNumber}: ${match1.matchId} and ${match2.matchId}`,
          );
        }
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get next available time slot for a given court
 */
/**
 * Run autoScheduleMatches `attempts` times with different tie-breaking seeds and
 * return the result with the highest fairness score.
 */
export function autoScheduleMatchesBestOf(
  tournament: Tournament,
  attempts: number = 8,
): { result: Tournament; bestScore: number; bestSeed: number } {
  let bestResult = tournament;
  let bestScore = -1;
  let bestSeed = 0;

  for (let seed = 0; seed < attempts; seed++) {
    const candidate = autoScheduleMatches(tournament, seed);
    const { fairnessScore } = analyzePlayerWaitingTimes(candidate);
    if (fairnessScore > bestScore) {
      bestScore = fairnessScore;
      bestResult = candidate;
      bestSeed = seed;
    }
  }

  console.log(`[MultiAttempt] Best seed: ${bestSeed}, fairness score: ${bestScore.toFixed(1)} (ran ${attempts} attempts)`);
  return { result: bestResult, bestScore, bestSeed };
}

export function getNextAvailableSlot(
  tournament: Tournament,
  courtNumber: number,
  afterTime?: string,
): { startTime: string; endTime: string } | null {
  const baseDate = tournament.startDate
    ? new Date(tournament.startDate)
    : new Date();

  const timeSlots = generateTimeSlots(tournament, baseDate);
  const courtSlots = timeSlots.filter(
    (slot) => slot.courtNumber === courtNumber && slot.available,
  );

  if (afterTime) {
    const afterDate = parseTime(afterTime, baseDate);
    const laterSlots = courtSlots.filter(
      (slot) => new Date(slot.startTime) > afterDate,
    );
    return laterSlots.length > 0 ? laterSlots[0] : null;
  }

  return courtSlots.length > 0 ? courtSlots[0] : null;
}

// Synthesizes extra "Round 1" entries for teams that structurally skipped Round 1
// (uneven bracket size) so the bracket UI can render a dedicated Bye card for them —
// no real match record exists for a skipped round, so these are display-only stubs.
export function withByeCards<T extends {
  matchNumber?: number;
  isConsolationMatch?: boolean;
  team1Id?: string;
  team2Id?: string;
  team1SkippedFirstRound?: boolean;
  team2SkippedFirstRound?: boolean;
}>(bracket: T[]): T[] {
  const byeCards: any[] = [];
  bracket.forEach((m) => {
    if (m.isConsolationMatch) return;
    if (m.team1SkippedFirstRound && m.team1Id) {
      byeCards.push({ id: `bye-${m.matchNumber}-1`, round: "Round 1", isByeCard: true, byeTeamId: m.team1Id });
    }
    if (m.team2SkippedFirstRound && m.team2Id) {
      byeCards.push({ id: `bye-${m.matchNumber}-2`, round: "Round 1", isByeCard: true, byeTeamId: m.team2Id });
    }
  });
  return byeCards.length ? [...bracket, ...byeCards] : bracket;
}
